import { autorun, makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import SubDependentDelegate from 'store/modules/subscriber/subscriber-basic-info/dependents/subDependentDelegate';
import { useEffect, useState } from 'react';
import SubscriberBasicInfoDelegate from '../subscriberBasicInfoDelegate';

interface DomainData {
  dependentDelegate: SubDependentDelegate;
  subscriberBasicInfo: SubscriberBasicInfoDelegate;
}

export class RemoveDependentModalStore {
  private readonly _dependentDelegate: SubDependentDelegate;
  private readonly _subscriberBasicInfo: SubscriberBasicInfoDelegate;
  private _removeDependentModal = new ModalDelegate();
  private _isLoading = false;

  public get showRemoveDependentModal(): boolean {
    return this._removeDependentModal.isVisible;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  private _setLoading(loadingStatus: boolean): void {
    this._isLoading = loadingStatus;
  }

  public openRemoveDependentModal(): void {
    this._removeDependentModal.open();
  }

  public closeRemoveDependentModal(): void {
    this._removeDependentModal.close();
  }

  public async removeDependent(): Promise<void> {
    this._setLoading(true);
    await this._subscriberBasicInfo.removeDependent(this._dependentDelegate.dependent.id);
    runInAction(() => {
      this._setLoading(false);
      this.closeRemoveDependentModal();
    });
  }

  constructor({ dependentDelegate, subscriberBasicInfo }: DomainData) {
    this._dependentDelegate = dependentDelegate;
    this._subscriberBasicInfo = subscriberBasicInfo;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export const useRemoveDependentModalStore = ({
  dependentDelegate,
  subscriberBasicInfo,
}: DomainData): RemoveDependentModalStore | null => {
  const [store, setStore] = useState<RemoveDependentModalStore | null>(null);

  useEffect(() => {
    autorun(() => {
      setStore(new RemoveDependentModalStore({ dependentDelegate, subscriberBasicInfo }));
    });

    return (): void => {
      setStore(null);
    };
  }, [dependentDelegate, subscriberBasicInfo]);

  return store;
};
